const PERMISSION_RETRY_DELAYS_MS = [1000, 2000, 4000];

/**
 * True for Firestore rules or App Check rejections surfaced as permission-denied.
 * @param {unknown} error
 * @returns {boolean}
 */
export function isFirestorePermissionDenied(error) {
  return error?.code === 'permission-denied';
}

/**
 * Subscribes with onSnapshot and, when the listener dies with permission-denied,
 * subscribes again after a short delay. Firestore does not resume a listener
 * after that error, so a brief App Check token gap otherwise sticks until remount.
 *
 * A successful snapshot resets the retry budget. Other errors are reported
 * immediately. permission-denied is reported only after the delays are used up.
 *
 * @param {Function} listen
 * @param {unknown} reference
 * @param {(snapshot: unknown) => void} onNext
 * @param {(error: unknown) => void} onError
 * @param {{ delaysMs?: number[] }} [options]
 * @returns {() => void}
 */
export function onSnapshotWithPermissionRetry(
  listen,
  reference,
  onNext,
  onError,
  options = {},
) {
  const delaysMs = options.delaysMs ?? PERMISSION_RETRY_DELAYS_MS;
  let cancelled = false;
  let failedAttempts = 0;
  let unsubscribeCurrent = null;
  let retryTimer = null;

  const startListening = () => {
    unsubscribeCurrent = listen(
      reference,
      (snapshot) => {
        failedAttempts = 0;
        onNext(snapshot);
      },
      (listenError) => {
        const delayMs = delaysMs[failedAttempts];
        const canRetry =
          !cancelled &&
          isFirestorePermissionDenied(listenError) &&
          typeof delayMs === 'number';
        if (canRetry) {
          failedAttempts += 1;
          retryTimer = setTimeout(() => {
            retryTimer = null;
            if (!cancelled) {
              startListening();
            }
          }, delayMs);
          return;
        }
        onError(listenError);
      },
    );
  };

  startListening();

  return () => {
    cancelled = true;
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    if (typeof unsubscribeCurrent === 'function') {
      unsubscribeCurrent();
    }
  };
}
